import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Image, Layer, Stage } from 'react-konva';

import {
  fetchFileComments,
  fetchFileVersionComments,
  fetchShareFileComments,
  fetchShareFileVersionComments,
  setFileCommentHighlighted,
  setFileCommentsAnnotateMode,
} from '../../../actions/Files';

import {
  APIupdateFileComment,
  APIupdateShareFileComment,
} from '../../../api/Files';

import FileViewTip from './Tip';
import FileViewFaceTip from './FaceTip';
import ImageAnnotation from './ImageAnnotation';

import { isMobile } from '../../../helpers/App';
import {
  eventSubscribe,
  eventUnsubscribe,
} from '../../../helpers/Events';
import { retrieveSizeAdjustedVideoAnnotations } from '../../../helpers/Files';

const AnnotationWrapper = ({
  type,
  displayImage,
  file,
  fileVersion,
  sidebarTab,
  comments,
  commentHighlighted,
  commentsAnnotateMode,
  loadShareComments,
  loadComments,
  share,
  changeCommentsAnnotateMode,
  handleParentAnnotationTip,
  highlightComment,
  emptyClick,
  currentTime,
  videoRef,
  updateCommentsCount,
  handleSidebarTab,
  readOnly,
}) => {
  const [newAnnotation, setNewAnnotation] = useState([]);
  const [tipAnnotation, setTipAnnotation] = useState(null);
  const [annotations, setAnnotations] = useState([]);
  const [showTip, setShowTip] = useState(false);
  const [tipPosition, setTipPosition] = useState({
    x: 0, y: 0, width: 0, height: 0,
  });
  const [isRectHovered, setIsRectHovered] = useState(false);
  const [isTipHovered, setIsTipHovered] = useState(false);
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [awaitToCreateAnnotation, setAwaitToCreateAnnotation] = useState(false);
  const [stageWidth, setStageWidth] = useState(0);
  const [stageHeight, setStageHeight] = useState(0);
  const [highlightedVideoAnnotationId, setHighlightedVideoAnnotationId] = useState(null);

  const fileWrapper = useRef(null);
  let tooltipTimeout = null;

  useEffect(() => {
    const faceUpdated = () => {
      setShowTip(false);
    };

    eventSubscribe('faceUpdated', faceUpdated);
    return () => {
      eventUnsubscribe('faceUpdated', faceUpdated);
    };
  }, []);

  const prepareAnnotations = (targetAnnotations) => {
    if (type === 'video') {
      const adjustedAnnotations =
        retrieveSizeAdjustedVideoAnnotations(targetAnnotations, stageWidth, stageHeight);

      const videoAnnotations = adjustedAnnotations.map(a => {
        if (a.id === highlightedVideoAnnotationId) {
          return {
            ...a,
            highlighted: true,
          };
        }
        return a;
      });

      return videoAnnotations;
    } if (type === 'image') {
      let adjustedAnnotations =
        retrieveSizeAdjustedVideoAnnotations(targetAnnotations, stageWidth, stageHeight);
      if (sidebarTab === 'people' && file.faces) {
        const scale = displayImage.naturalWidth / file.attributes.width;
        const faceAnnotations = file.faces.filter(f => (!f.hidden)).map(f => ({
          id: f.uuid,
          meta: {
            x: f.position.x * scale,
            y: f.position.y * scale,
            width: f.position.w * scale,
            height: f.position.h * scale,
            stageHeight: displayImage.naturalHeight,
            stageWidth: displayImage.naturalWidth,
          },
          type: 'face',
          identified: !!f.person?.name,
        }));
        adjustedAnnotations = adjustedAnnotations.concat(
          retrieveSizeAdjustedVideoAnnotations(faceAnnotations, stageWidth, stageHeight),
        );
      }

      return adjustedAnnotations;
    }
    return targetAnnotations;
  };

  const annotationsToDraw = prepareAnnotations([...annotations, ...newAnnotation]);
  const annotationStrokeMargin = 3;

  const getMeta = (meta) => {
    if (type !== 'video') return meta;

    const videoMeta = {
      x: meta.x,
      y: meta.y,
      width: meta.width,
      height: meta.height,
      duration: meta.duration ? meta.duration : null,
      time: meta.time,
      stageWidth,
      stageHeight,
    };

    return videoMeta;
  };

  const handleMouseEnter = (e) => {
    e.target.getStage().container().style.cursor = commentsAnnotateMode ? 'crosshair' : 'default';
  };

  const checkIfPointerInAnnotation = (pointerX, pointerY) => annotationsToDraw.some(a => {
    const {
      x, y, width, height,
    } = a.meta;

    const left = x - annotationStrokeMargin;
    const right = x + width + annotationStrokeMargin;
    const top = y - annotationStrokeMargin;
    const bottom = y + height + annotationStrokeMargin;

    if (pointerX > left && pointerX < right && pointerY > top && pointerY < bottom) {
      return true;
    }
    return false;
  });

  const handleMouseDown = event => {
    setIsMouseDown(true);
    setShowTip(false);
    if (commentHighlighted) {
      highlightComment(null);
    }
    if (!commentsAnnotateMode) {
      setNewAnnotation([]);
      emptyClick();
      return;
    }
    if (newAnnotation.length === 0) {
      const { x, y } = event.target.getStage().getPointerPosition();
      if (checkIfPointerInAnnotation(x, y)) {
        setNewAnnotation([]);
      } else {
        const newAnnotations = annotations.filter(a => a.id !== 'new');
        setAnnotations(newAnnotations);

        const id = 'new';
        setNewAnnotation([{
          id,
          meta: {
            x,
            y,
            width: 0,
            height: 0,
          },
        }]);
      }
    }
  };

  const handleMouseMove = event => {
    if (newAnnotation.length === 1) {
      const sx = newAnnotation[0].meta.x;
      const sy = newAnnotation[0].meta.y;
      const { x, y } = event.target.getStage().getPointerPosition();
      const id = 'new';
      if (newAnnotation) {
        setNewAnnotation([{
          id,
          meta: {
            x: sx,
            y: sy,
            width: x - sx,
            height: y - sy,
          },
        }]);
      }
    }
  };

  const handleMouseUp = () => {
    setIsMouseDown(false);
    if (newAnnotation.length === 1) {
      const annotationToPush = newAnnotation[0];

      const { x } = annotationToPush.meta;
      const { y } = annotationToPush.meta;
      const { width } = annotationToPush.meta;
      const { height } = annotationToPush.meta;

      if (width < 5 && height < 5) {
        setNewAnnotation([]);
        return;
      }

      if (width < 0) {
        annotationToPush.meta.x = x + width;
        annotationToPush.meta.width = -width;
      }
      if (height < 0) {
        annotationToPush.meta.y = y + height;
        annotationToPush.meta.height = -height;
      }

      const newAnnotations = annotations.filter(a => a.id !== 'new');

      newAnnotations.push(annotationToPush);

      setAwaitToCreateAnnotation(true);
      setTipAnnotation(null);
      setNewAnnotation([]);
      setAnnotations(newAnnotations);
      setTipPosition(annotationToPush.meta);
      setShowTip(true);
    }
  };

  const handleMouseEnterAnnotation = (e, position, id, annotationType) => {
    clearTimeout(tooltipTimeout);
    if (id === 'new') {
      setTipAnnotation(null);
      const annotationNew = annotations.find(a => a.id === id);
      if (annotationNew && !isMouseDown) {
        setTipPosition(position);
        setTipAnnotation(null);
        setShowTip(true);
        setIsRectHovered(true);
      }
    } else {
      const findTipAnnotation = annotationType === 'face' ?
        file.faces.find(f => (f.uuid === id)) :
        comments.find(c => c.id === id);
      if (findTipAnnotation && !isMouseDown) {
        setShowTip(true);
        setIsRectHovered(true);
        setTipPosition(position);
        setTipAnnotation({ ...findTipAnnotation, type: annotationType });
      } else {
        setTipAnnotation(null);
      }
    }
  };

  const handleMouseLeaveAnnotation = () => {
    if (!(showTip && tipAnnotation === null)) {
      clearTimeout(tooltipTimeout);
      tooltipTimeout = setTimeout(() => {
        setIsRectHovered(false);
      }, 500);
    }
  };

  const updateComment = (editId, meta) => {
    if (editId === 'new') {
      const filterAnnotations = annotations.filter(a => a.id !== 'new');
      const newAnnotations = [...filterAnnotations, { id: editId, meta: getMeta(meta) }];
      setAnnotations([]);
      setAnnotations(newAnnotations);
      setAwaitToCreateAnnotation(false);
    } else if (share) {
      APIupdateShareFileComment(share.uuid, file.uuid, {
        id: editId,
        meta: getMeta(meta),
      }).then(() => {
        loadShareComments(share.uuid, file.uuid, fileVersion, true);
      });
    } else {
      APIupdateFileComment({
        id: editId,
        meta: getMeta(meta),
      }).then(() => {
        loadComments(file.uuid, fileVersion, true).then((commentsResponse) => {
          updateCommentsCount(commentsResponse.comments_count, fileVersion);
        });
      });
    }
  };

  const createCommentCallback = (annotation) => {
    setAwaitToCreateAnnotation(false);
    setTipAnnotation(annotation);
  };

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      if (showTip) {
        setShowTip(false);
        const newAnnotations = annotations.filter(a => a.id !== 'new');
        setAnnotations(newAnnotations);
        if (awaitToCreateAnnotation) {
          setAwaitToCreateAnnotation(false);
        }
      } else if (commentsAnnotateMode) {
        changeCommentsAnnotateMode(false);
      }
    }
  }, [
    annotations,
    awaitToCreateAnnotation,
    changeCommentsAnnotateMode,
    commentsAnnotateMode,
    showTip,
  ]);

  useEffect(() => {
    const handleImageStage = () => {
      const wrapper = document.querySelector('.file-wrapper-image') || document.querySelector('.file-wrapper-file');

      const wrapperWidth = wrapper.offsetWidth;
      const wrapperHeight = wrapper.offsetHeight;

      const imageWidth = displayImage.width;
      const imageHeight = displayImage.height;

      const imageAspectRatio = imageWidth / imageHeight;
      const wrapperAspectRatio = wrapperWidth / wrapperHeight;

      let width = imageWidth;
      let height = imageHeight;

      // Apply only if image is overflowing wrapper
      if (imageWidth > wrapperWidth || imageHeight > wrapperHeight) {
        if (imageAspectRatio > wrapperAspectRatio) {
          // Fit to width
          width = wrapperWidth;
          height = width / imageAspectRatio;
        } else {
          // Fit to height
          height = wrapperHeight;
          width = height * imageAspectRatio;
        }
      }

      setStageHeight(height);
      setStageWidth(width);
    };

    if (type === 'video') {
      const videoElement = videoRef.current;

      const updateDimensions = () => {
        if (!videoElement || !document.body.contains(videoElement)) {
          return;
        }
        setStageWidth(videoElement.offsetWidth);
        setStageHeight(videoElement.offsetHeight);
      };

      const resizeObserver = new ResizeObserver(updateDimensions);

      if (videoElement && document.body.contains(videoElement)) {
        resizeObserver.observe(videoElement);
        updateDimensions();
      }

      return () => {
        if (videoElement) {
          resizeObserver.unobserve(videoElement);
        }
      };
    }
    if (type === 'image') {
      const fileElement = fileWrapper.current;

      handleImageStage();

      const updateDimensions = () => {
        if (isMobile() && fileElement.offsetWidth < displayImage.width) {
          // For images wider than available space
          setStageWidth(fileElement.offsetWidth);
          setStageHeight(displayImage.height * (fileElement.offsetWidth / displayImage.width));
        } else {
          handleImageStage();
        }
      };

      const resizeObserver = new ResizeObserver(updateDimensions);

      const body = document.querySelector('body');
      resizeObserver.observe(body);

      return () => {
        resizeObserver.unobserve(body);
      };
    }
    return () => {};
  }, [displayImage, type, videoRef]);

  useEffect(() => {
    const handleKeyDownListener = (event) => {
      handleKeyDown(event);
    };
    document.addEventListener('keydown', handleKeyDownListener);
    return () => {
      document.removeEventListener('keydown', handleKeyDownListener);
    };
  }, [handleKeyDown]);

  useEffect(() => {
    if (!isTipHovered && !isRectHovered) {
      setShowTip(false);
    }
  }, [isRectHovered, isTipHovered]);

  useEffect(() => {
    let filteredAnnotations = comments.filter(c => (c.type === type && c.meta));
    if (type === 'video') {
      const offset = 0.3;
      filteredAnnotations = filteredAnnotations.filter(c => {
        if (c.meta.duration) {
          return currentTime >= c.meta.time && currentTime <= c.meta.time + c.meta.duration;
        }
        return currentTime >= c.meta.time && currentTime <= c.meta.time + offset;
      });
    }
    setAnnotations(filteredAnnotations);
  }, [comments, type, currentTime]);

  useEffect(() => {
    if (type === 'video' && showTip && tipAnnotation && tipAnnotation.meta) {
      const { time, duration } = tipAnnotation.meta;
      if (time && duration && !(currentTime >= time && currentTime <= time + duration)) {
        setShowTip(false);
        setIsTipHovered(false);
      }
    }
  }, [currentTime, showTip, tipAnnotation, type]);

  useEffect(() => {
    if (commentHighlighted && (commentHighlighted.type === 'image' || commentHighlighted.type === 'video')) {
      if (commentHighlighted.type === 'video') {
        const comment = comments.find(c => c.id === commentHighlighted.commentId);
        const { time } = comment.meta;

        videoRef.current.currentTime(time);
        setHighlightedVideoAnnotationId(commentHighlighted.commentId);
      } else {
        const newAnnotations = annotations.map(a => {
          if (a.id === commentHighlighted.commentId) {
            return { ...a, highlighted: true };
          }
          return { ...a, highlighted: false };
        });
        setAnnotations(newAnnotations);
      }
    }
  }, [annotations, commentHighlighted, comments, videoRef]);

  useEffect(() => {
    handleParentAnnotationTip(showTip);
  }, [handleParentAnnotationTip, showTip]);

  useEffect(() => {
    if (!commentsAnnotateMode && showTip && awaitToCreateAnnotation) {
      const newAnnotations = annotations.filter(a => a.id !== 'new');
      setShowTip(false);
      setAnnotations(newAnnotations);
    }
  }, [annotations, awaitToCreateAnnotation, commentsAnnotateMode, showTip]);

  return (
    <div className={`file-${type}-wrapper`} ref={fileWrapper}>
      <div className={`file-${type}-annotation-wrapper`}>
        <Stage
          width={stageWidth}
          height={stageHeight}
          onMouseEnter={handleMouseEnter}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
        >
          <Layer>
            {displayImage && (
              <Image
                image={displayImage}
                width={stageWidth}
                height={stageHeight}
                preventDefault={false}
              />
            )}
            {annotationsToDraw.map((annotation) => (
              <ImageAnnotation
                key={annotation.id}
                shapeProps={annotation.meta}
                annotationId={annotation.id}
                highlighted={annotation.highlighted || false}
                identified={annotation.identified}
                stageWidth={stageWidth}
                stageHeight={stageHeight}
                type={annotation.type}
                onChange={newAttrs => {
                  const newAnnotations = annotations.map(a => {
                    const newMeta = { ...newAttrs.meta, stageWidth, stageHeight };

                    if (a.id === newAttrs.id) {
                      updateComment(a.id, getMeta(newMeta));
                      return {
                        id: a.id,
                        meta: newMeta,
                      };
                    }
                    return a;
                  });
                  setAnnotations([]);
                  setAnnotations(newAnnotations);
                }}
                handleMouseEnter={handleMouseEnterAnnotation}
                handleMouseLeave={handleMouseLeaveAnnotation}
                commentsAnnotateMode={commentsAnnotateMode}
                annotationStrokeMargin={annotationStrokeMargin}
              />
            ))}
          </Layer>
        </Stage>
        {showTip && (
          <>
            {tipAnnotation?.type === 'face' ? (
              <div
                style={{
                  position: 'absolute',
                  left: tipPosition.x + tipPosition.width,
                  top: tipPosition.y - 20,
                }}
                onMouseEnter={() => { setIsTipHovered(true); }}
                onMouseLeave={() => { setTimeout(() => { setIsTipHovered(false); }, 500); }}
              >
                <div className="face-tip-element">
                  <div className="face-tip-wrapper">
                    <FileViewFaceTip
                      file={file}
                      highlight={tipAnnotation}
                      meta={{
                        ...tipPosition, time: currentTime, stageWidth, stageHeight,
                      }}
                      readOnly={readOnly}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div
                style={{
                  position: 'absolute',
                  left: (tipPosition.x + (tipPosition.width / 2)) - 177,
                  bottom: tipPosition.y >= 200 ? stageHeight - tipPosition.y : 'unset',
                  top: tipPosition.y < 200 ? tipPosition.y + tipPosition.height : 'unset',
                }}
                onMouseEnter={() => { setIsTipHovered(true); }}
                onMouseLeave={() => { setIsTipHovered(false); }}
              >
                <div className={`${type}-comment-tip-wrapper`}>
                  <FileViewTip
                    file={file}
                    fileVersion={fileVersion}
                    highlight={tipAnnotation}
                    type={type}
                    meta={{
                      ...tipPosition, time: currentTime, stageWidth, stageHeight,
                    }}
                    createCommentCallback={createCommentCallback}
                    updateCommentsCount={updateCommentsCount}
                    handleSidebarTab={handleSidebarTab}
                    readOnly={readOnly}
                  />
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = state => ({
  comments: state.Files.comments,
  commentsView: state.Files.commentsView,
  commentsOrder: state.Files.commentsOrder,
  commentHighlighted: state.Files.commentHighlighted,
  commentsAnnotateMode: state.Files.commentsAnnotateMode,
  share: state.Shares.share,
});

const mapDispatchToProps = dispatch => ({
  loadComments: (fileUuid, fileVersion = null, silent = false) => {
    if (fileVersion) {
      return dispatch(fetchFileVersionComments(fileUuid, fileVersion, silent));
    }
    return dispatch(fetchFileComments(fileUuid, silent));
  },
  loadShareComments: (shareUuid, fileUuid, fileVersion, silent = false) => {
    if (fileVersion) {
      return dispatch(fetchShareFileVersionComments(shareUuid, fileUuid, fileVersion, silent));
    }
    return dispatch(fetchShareFileComments(shareUuid, fileUuid, silent));
  },
  highlightComment: (commentHighlighted) => {
    dispatch(setFileCommentHighlighted(commentHighlighted));
  },
  changeCommentsAnnotateMode: (commentsAnnotateMode) => (
    dispatch(setFileCommentsAnnotateMode(commentsAnnotateMode))
  ),
});

AnnotationWrapper.propTypes = {
  type: PropTypes.string.isRequired,
  file: PropTypes.object.isRequired,
  fileVersion: PropTypes.number,
  sidebarTab: PropTypes.string.isRequired,
  displayImage: PropTypes.object,
  comments: PropTypes.array.isRequired,
  loadComments: PropTypes.func.isRequired,
  loadShareComments: PropTypes.func.isRequired,
  commentHighlighted: PropTypes.object,
  commentsAnnotateMode: PropTypes.bool.isRequired,
  changeCommentsAnnotateMode: PropTypes.func.isRequired,
  handleParentAnnotationTip: PropTypes.func.isRequired,
  highlightComment: PropTypes.func.isRequired,
  share: PropTypes.object,
  emptyClick: PropTypes.func,
  currentTime: PropTypes.number,
  videoRef: PropTypes.object,
  updateCommentsCount: PropTypes.func.isRequired,
  handleSidebarTab: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
};

AnnotationWrapper.defaultProps = {
  fileVersion: null,
  displayImage: null,
  commentHighlighted: null,
  share: null,
  emptyClick: () => {},
  currentTime: 0,
  videoRef: null,
  readOnly: false,
};

export default connect(mapStateToProps, mapDispatchToProps)(AnnotationWrapper);
